import { useState } from "react";
import { Button, Icon } from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { serverGql } from "__generated__/serverGql";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";
import serverClient from "utils/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { useUpdateBrandSubscriptionCache } from "./useRefreshSubscriptionStatus";

const fragment = gql(/* GraphQL */ `
  fragment StartFreeTrialButton on Brand {
    id
    stripeCustomerId
    stripePriceId
    subscriptionTrialDays
  }
`);

const mutation = serverGql(/* ServerGraphQL */ `
  mutation StartStripeFreeTrial($brandId: UUID!) {
    startStripeFreeTrial(brandId: $brandId) {
      brand {
        id
        isSubscriptionValid
        stripeSubscriptionId
        ...RefreshBrandSubscriptionFields
      }
    }
  }
`);

type Props = {
  brand: FragmentType<typeof fragment>;
};

export default function StartFreeTrialButton({ brand: brandFragment }: Props) {
  const brand = getFragmentData(fragment, brandFragment);
  const updateBrandSubscriptionCache = useUpdateBrandSubscriptionCache();

  const [isLoading, setIsLoading] = useState(false);

  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const handleClick = () => {
    setIsLoading(true);

    serverClient
      .mutate({ mutation, variables: { brandId: brand.id } })
      .then((response) => {
        const updatedBrand = response.data?.startStripeFreeTrial?.brand;
        assertPresence(updatedBrand, "Expected brand to be defined");
        updateBrandSubscriptionCache(updatedBrand);
        showSuccessToast("Free trial started");
      })
      .catch((reason: unknown) => {
        showErrorToast(reason);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Button
      colorScheme="pinkButton"
      isDisabled={
        !brand?.stripeCustomerId ||
        !brand?.stripePriceId ||
        !brand?.subscriptionTrialDays
      }
      isLoading={isLoading}
      onClick={handleClick}
      rightIcon={<Icon as={MdArrowForward} mr={-2} />}
      mb={6}
    >
      Start Free Trial
    </Button>
  );
}
